import { useCallback, useEffect, useRef } from "react";
import type {
  CreateArgsSF,
  SecureFieldInstance,
} from "@yuno-payments/sdk-web-types";
import useCallbackRef from "@shared/hooks/useCallbackRef/useCallbackRef";
// eslint-disable-next-line import/no-cycle
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
// eslint-disable-next-line import/no-cycle
import { getStripeColors } from "@shared/theme/theme";
import { isDarkMode } from "@shared/theme/utils/color-mode";
import YunoPaymentGateway from "../../../gateways/YunoPaymentGateway/YunoPaymentGateway";

/**
 * A containing element for Yuno Secure Fields to inject their elements into.
 * Accepts an `element` prop which tells Yuno what kind of secure field to create (pan, cvv, expiration),
 * and exposes `onChange` / `onElementCreate` callbacks wired into the Yuno field options.
 */
function YunoElement({
  element,
  elementProps,
  onChange,
  onElementCreate,
  elementId,
}: YunoElementProps) {
  const { manager } = usePaymentManager();
  const domElementRef = useRef<HTMLDivElement>(null);

  const yunoElementRef = useRef<ReturnType<
    SecureFieldInstance["create"]
  > | null>();

  const onChangeRef = useCallbackRef(onChange);
  const onElementCreateRef = useCallbackRef(onElementCreate);
  const hasCalledCreateAndMount = useRef(false);

  const gateway = manager.handler?.gateway;
  const yunoGateway =
    gateway instanceof YunoPaymentGateway ? gateway : undefined;
  const elementsReady = yunoGateway?.elementsInstance;

  const createAndMountElement = useCallback(() => {
    if (hasCalledCreateAndMount.current) return;
    if (!yunoGateway?.elementsInstance) return;
    if (!domElementRef.current) return;

    try {
      hasCalledCreateAndMount.current = true;

      if (!yunoElementRef.current) {
        const dark = isDarkMode();
        const yunoColors = getStripeColors(dark ? "dark" : "light");

        const options: CreateArgsSF = {
          ...elementProps,
          name: element,
          options: {
            ...elementProps?.options,
            onChange: (
              ...args: Parameters<
                NonNullable<CreateArgsSF["options"]["onChange"]>
              >
            ) => onChangeRef.current?.(...args),
            onRenderedSecureField: (
              ...args: Parameters<
                NonNullable<CreateArgsSF["options"]["onRenderedSecureField"]>
              >
            ) => onElementCreateRef.current?.(...args),
            showError: false,
            designType: "label-placeholder",
            // Matches iframe content height to other payment-step inputs (element.3xl = 40px).
            // Hardcoded colors since the iframe can't access parent CSS variables.
            styles: `
              body {
                margin: 0px !important;
                padding: 0px !important;
              }

              #root {
                .Yuno-fieldset__box {
                  border: none;
                  background-color: ${yunoColors.bg};

                  .Yuno-input__content {
                    height: auto;
                    padding: 0px;
                    background-color: ${yunoColors.bg};

                    input {
                      background-color: ${yunoColors.bg};
                      color: ${
                        dark ? yunoColors.fontLight : yunoColors.fontDark
                      };
                      line-height: normal !important;
                    }

                    input::placeholder {
                      color: ${yunoColors.muted};
                    }

                  }
                }

                .Yuno-fieldset__error {
                  display: none;
                }

                ${elementProps?.options?.styles ?? ""}
              }
            `,
          },
        };

        yunoElementRef.current = yunoGateway.elementsInstance.create(options);
      }

      yunoElementRef.current.render(`#${domElementRef.current.id}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [yunoGateway, elementsReady, element]);

  useEffect(() => {
    createAndMountElement();
  }, [createAndMountElement]);

  useEffect(() => {
    return () => {
      yunoElementRef.current?.unmountSync();
      yunoElementRef.current = null;
      hasCalledCreateAndMount.current = false;
    };
  }, []);

  const domId = elementId ?? `yuno-element-${element}`;

  return (
    <>
      <div ref={domElementRef} id={domId} data-testid={domId} />
    </>
  );
}

export default YunoElement;

export type YunoElementProps = {
  element: CreateArgsSF["name"];
  elementProps?: CreateArgsSF;
  onChange?: CreateArgsSF["options"]["onChange"];
  onElementCreate?: CreateArgsSF["options"]["onRenderedSecureField"];
  /**
   * Custom DOM ID for the container element.
   * Defaults to `yuno-element-${element}`.
   * Use this when multiple instances of the same element type exist in the DOM
   * to avoid duplicate ID conflicts.
   */
  elementId?: string;
};
